<script setup>
import { ref, onMounted, defineProps, defineEmits } from 'vue';
import Data from '@/api/data';
import SetupCalculation from '@/components/modals/SetupCalculation.vue';

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  dataID: {
    type: String,
    required: true
  }
});

const emit = defineEmits(['update:modelValue']);

// Local state
const config = ref({ ...props.modelValue });
const loadingData = ref(false);
const subcollections = ref({});
const validFields = ref([]);
const showCalcModal = ref(false);

// Lifecycle
onMounted(async () => {
  await getSubcollections();
  
  if (config.value.source) {
    updateValidFields(config.value.source);

    if (!config.value.specifications) {
      config.value.specifications = {};
    }
    if (!config.value.specifications[config.value.source] || config.value.specifications[config.value.source].length === 0) {
      config.value.specifications[config.value.source] = [{ field: '', friendlyName: '', decimals: 2, unit: '' }];
      emitUpdate();
    }
  }
});

// API Methods
const getSubcollections = async () => {
  loadingData.value = true;
  try {
    const response = await Data.get(props.dataID);
    if (response.body.status === 'success') {
      subcollections.value = Object.fromEntries(
        Object.entries(response.body.details)
          .sort(([a], [b]) => a.localeCompare(b))
      );
    }
  } catch (err) {
    console.error('Error fetching subcollections:', err);
  } finally {
    loadingData.value = false;
  }
};

const updateValidFields = (source) => {
  if (source && subcollections.value[source]) {
    // Store both field name and type
    validFields.value = Object.entries(subcollections.value[source])
      .map(([field, type]) => ({ field, type }))
      .sort((a, b) => a.field.localeCompare(b.field));
  } else {
    validFields.value = [];
  }
};

// Helper to get field type
const getFieldType = (fieldName) => {
  const fieldInfo = validFields.value.find(f => f.field === fieldName);
  return fieldInfo?.type || 'string';
};

// Helper to check if field is numeric
const isNumericField = (fieldName) => {
  const type = getFieldType(fieldName);
  return type === 'int' || type === 'float';
};

const handleSourceChange = (newSource) => {
  if (newSource === '__new_source__') {
    showCalcModal.value = true;
    return;
  }
  config.value.source = newSource;
  updateValidFields(newSource);
  
  if (!config.value.specifications) {
    config.value.specifications = {};
  }
  if (!config.value.specifications[newSource] || config.value.specifications[newSource].length === 0) {
    config.value.specifications[newSource] = [{ field: '', friendlyName: '', decimals: 2, unit: '' }];
  }
  
  emitUpdate();
};

const addValue = () => {
  if (!config.value.source) return;
  
  if (!config.value.specifications) {
    config.value.specifications = {};
  }
  if (!config.value.specifications[config.value.source]) {
    config.value.specifications[config.value.source] = [];
  }
  
  config.value.specifications[config.value.source].push({
    field: '',
    friendlyName: '',
    decimals: 2,
    unit: ''
  });
  
  emitUpdate();
};

const removeValue = (index) => {
  if (!config.value.source || !config.value.specifications[config.value.source]) return;
  
  config.value.specifications[config.value.source].splice(index, 1);
  emitUpdate();
};

const emitUpdate = () => {
  emit('update:modelValue', config.value);
};

const handleNewSource = ({ name }) => {
  showCalcModal.value = false;
  getSubcollections().then(() => {
    handleSourceChange(name.replace(/\s+/g, '_'));
  });
};

</script>

<template>
  <div class="space-y-4">
    <!-- Data Source -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        Data Source *
        <span v-if="loadingData" class="inline-flex items-center ml-2" title="Loading options...">
          <span class="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></span>
        </span>
      </label>
      <select
        :value="config.source"
        @change="handleSourceChange($event.target.value)"
        :disabled="loadingData"
        class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <option value="__new_source__">+ New Source</option>
        <option value="">Select a source...</option>
        <option v-for="source in Object.keys(subcollections)" :key="source" :value="source">
          {{ source }}
        </option>
      </select>
    </div>

    <!-- Values Section -->
    <div v-if="config.source">

        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Values to Display
        </label>

        <!-- Value Configurations -->
        <div v-for="(spec, index) in config.specifications?.[config.source] ?? []" :key="index" class="p-4 border border-gray-300 dark:border-gray-600 rounded-lg mb-3 space-y-3">
          <div class="flex justify-between items-start">
            <h4 class="text-sm font-semibold text-gray-700 dark:text-gray-300">Value {{ index + 1 }}</h4>
            <button
              @click="removeValue(index)"
              type="button"
              class="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
            >
              Remove
            </button>
          </div>

          <!-- Field Selection -->
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Field Name *
            </label>
            <select
              v-model="spec.field"
              @change="emitUpdate"
              :disabled="loadingData"
              class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">Select a field...</option>
              <option v-for="fieldInfo in validFields" :key="fieldInfo.field" :value="fieldInfo.field">
                {{ fieldInfo.field }}
              </option>
            </select>
          </div>

          <!-- Friendly Name -->
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Display Label
            </label>
            <input
              v-model="spec.friendlyName"
              type="text"
              placeholder="e.g., Temperature"
              @input="emitUpdate"
              class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <!-- Unit and Decimals (only for numeric fields) -->
          <div v-if="spec.field && isNumericField(spec.field)" class="grid grid-cols-2 gap-2">
            <div>
              <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Unit
              </label>
              <input
                v-model="spec.unit"
                type="text"
                placeholder="e.g., °F"
                @input="emitUpdate"
                class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Decimals
              </label>
              <input
                v-model.number="spec.decimals"
                type="number"
                min="0"
                max="10"
                @input="emitUpdate"
                class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        <button
          @click="addValue"
          type="button"
          class="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
        >
          + Add Value
        </button>
      </div>
  </div>

  <SetupCalculation
    v-if="showCalcModal"
    :dataID="dataID"
    :subcollectionOptions="subcollections"
    @calculation-details="handleNewSource"
    @close="showCalcModal = false"
  />
</template>
